import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PhoneCall, ClipboardList, BadgeCheck, MessageSquareText, Send } from "lucide-react";

const MESSAGES = [
  {
    testId: "whatsapp-option-callback",
    icon: PhoneCall,
    label: "Request a callback",
    text: "Hi Impact Sutra! Please call me back — I'd like to discuss CSR fundraising support for my NGO.",
  },
  {
    testId: "whatsapp-option-plans",
    icon: ClipboardList,
    label: "Ask about association plans",
    text: "Hi Impact Sutra! I'd like to know more about your association plans for NGOs.",
  },
  {
    testId: "whatsapp-option-eligibility",
    icon: BadgeCheck,
    label: "Check my CSR eligibility",
    text: "Hi Impact Sutra! I'd like a free eligibility audit for my NGO.",
  },
  {
    testId: "whatsapp-option-general",
    icon: MessageSquareText,
    label: "General enquiry",
    text: "Hi Impact Sutra! I have a question about your CSR fundraising services.",
  },
];

const waLink = (text) => `https://wa.me/917906028651?text=${encodeURIComponent(text)}`;

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-50 w-80 overflow-hidden rounded-3xl border border-[#E6E9E6] bg-white shadow-2xl"
          >
            <div className="bg-[#0E2A6B] px-5 py-4">
              <p className="text-sm font-bold text-white">Impact Sutra</p>
              <p className="mt-0.5 text-xs text-sky-200">
                Typically replies within 24 business hours — or leave your number and we'll call you back.
              </p>
            </div>
            <div className="space-y-2 p-4">
              {MESSAGES.map((m) => (
                <a
                  key={m.testId}
                  data-testid={m.testId}
                  href={waLink(m.text)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-2xl border border-[#E6E9E6] bg-[#FBFBF9] px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#25D366] hover:bg-[#F0FBF4] hover:shadow-md"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#EAF1FC] text-[#0E2A6B]">
                    <m.icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 text-sm font-semibold text-slate-800">{m.label}</span>
                  <Send className="h-3.5 w-3.5 text-slate-400" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="whatsapp-float-button"
        onClick={() => setOpen(!open)}
        aria-label="Chat with Impact Sutra on WhatsApp"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-0 rounded-full p-4 transition-shadow ${
          open
            ? "bg-[#0E2A6B] shadow-[0_10px_35px_-8px_rgba(14,42,107,0.6)]"
            : "bg-[#25D366] shadow-[0_10px_35px_-8px_rgba(37,211,102,0.7)] hover:shadow-[0_14px_45px_-8px_rgba(37,211,102,0.85)]"
        }`}
      >
        {!open && (
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#25D366] opacity-25" />
        )}
        {open ? (
          <X className="h-7 w-7 text-white" />
        ) : (
          <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        )}
      </motion.button>
    </>
  );
}
